/**
 * Procedural terrain generator ported from cartografika-poc.
 * Region-aware: Sydney CBD, Blue Mountains, Daintree, Brisbane Hills, Custom.
 */

import type { RawTerrainData } from '../stores/useProceduralStore';

// ==========================================
// REGION-SPECIFIC GENERATORS
// ==========================================

function generateSydneyCBD(x: number, y: number): { baseZ: number; canopyZ: number } {
  const baseZ = 0.05 * Math.sin(x * 0.5) * Math.cos(y * 0.5);
  let canopyZ = 0;
  if (Math.abs(x - 2) < 1.2 && Math.abs(y - 2) < 1.2) { canopyZ = 8.5; }
  if (Math.abs(x + 2.2) < 1 && Math.abs(y + 2.2) < 1) { canopyZ = 12; }
  if (Math.abs(x - 2.5) < 0.8 && Math.abs(y + 2.5) < 0.8) { canopyZ = 6; }
  if (Math.abs(x + 1) < 0.6 && Math.abs(y - 2.5) < 0.6) { canopyZ = 9; }
  return { baseZ, canopyZ };
}

function generateBlueMountains(x: number, y: number): { baseZ: number; canopyZ: number } {
  const cliffLine = x - y;
  let baseZ = 0;
  let canopyZ = 0;

  if (cliffLine > 0) {
    baseZ = 2.5 + Math.sin(x * 1.5) * 0.2;
  } else {
    baseZ = -1.2 + Math.cos(y * 1.5) * 0.3;
  }

  if (Math.abs(cliffLine) < 0.4) {
    const t = (cliffLine + 0.4) / 0.8;
    baseZ = -1.2 * (1 - t) + 2.5 * t;
  }

  if (cliffLine < -0.5) {
    const trees = [
      { tx: -2.5, ty: 1, th: 9, tsig: 0.5 },
      { tx: -1, ty: 2.2, th: 7.5, tsig: 0.6 },
      { tx: -3, ty: -2.5, th: 8, tsig: 0.55 },
    ];
    for (const t of trees) {
      const dsq = (x - t.tx) ** 2 + (y - t.ty) ** 2;
      canopyZ += t.th * Math.exp(-dsq / (2 * t.tsig * t.tsig));
    }
  }

  return { baseZ, canopyZ };
}

function generateDaintree(x: number, y: number): { baseZ: number; canopyZ: number } {
  const baseZ = -0.3 + 0.08 * Math.sin(x * 0.4) * Math.cos(y * 0.4);
  let canopyZ = 0;

  const jungleTrees = [
    { tx: -2, ty: -2, th: 8, tsig: 0.8 },
    { tx: -2.2, ty: 0.5, th: 6.5, tsig: 0.6 },
    { tx: 0, ty: -2.5, th: 7, tsig: 0.75 },
    { tx: 2.5, ty: -1.5, th: 9, tsig: 0.9 },
    { tx: 2, ty: 1.5, th: 7.5, tsig: 0.7 },
    { tx: -0.5, ty: 2.5, th: 8.5, tsig: 0.8 },
    { tx: -3, ty: 2, th: 6, tsig: 0.5 },
    { tx: 0.5, ty: 0.5, th: 5.5, tsig: 0.65 },
    { tx: 3, ty: 3, th: 8, tsig: 0.75 },
    { tx: -1.5, ty: -3.5, th: 7, tsig: 0.7 },
  ];

  for (const t of jungleTrees) {
    const dsq = (x - t.tx) ** 2 + (y - t.ty) ** 2;
    canopyZ += t.th * Math.exp(-dsq / (2 * t.tsig * t.tsig));
  }

  return { baseZ, canopyZ };
}

function generateBrisbaneHills(x: number, y: number): { baseZ: number; canopyZ: number } {
  const baseZ = 0.8 * Math.sin(x * 0.7) * Math.cos(y * 0.7) + 0.3 * Math.sin(x * 1.8 + y * 1.2);
  let canopyZ = 0;

  const hillsTrees = [
    { tx: -1.5, ty: -1.5, th: 5.5, tsig: 0.6 },
    { tx: 1.8, ty: -0.5, th: 6, tsig: 0.7 },
    { tx: -2, ty: 1.5, th: 5, tsig: 0.55 },
    { tx: 1, ty: 2.5, th: 6.5, tsig: 0.65 },
  ];

  for (const t of hillsTrees) {
    const dsq = (x - t.tx) ** 2 + (y - t.ty) ** 2;
    canopyZ += t.th * Math.exp(-dsq / (2 * t.tsig * t.tsig));
  }

  return { baseZ, canopyZ };
}

function generateCustomCrop(
  x: number,
  y: number,
  rand: () => number
): { baseZ: number; canopyZ: number } {
  const angle = rand() * Math.PI * 2;
  const slopeStrength = 0.08 + rand() * 0.15;
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);

  let baseZ = slopeStrength * (x * dx + y * dy) + 0.4 * Math.sin(x * (0.3 + rand() * 0.5)) * Math.cos(y * (0.3 + rand() * 0.5));
  let canopyZ = 0;

  if (rand() > 0.45) {
    const scarpOffset = -1.5 + rand() * 3;
    if (x * dx + y * dy >= scarpOffset) {
      baseZ += 1.2 + rand() * 1.2;
    }
  }

  const numTrees = 2 + Math.floor(rand() * 5);
  for (let i = 0; i < numTrees; i++) {
    const tx = -3.5 + rand() * 7;
    const ty = -3.5 + rand() * 7;
    const th = 5 + rand() * 4.5;
    const tsig = 0.45 + rand() * 0.4;
    const dsq = (x - tx) ** 2 + (y - ty) ** 2;
    canopyZ += th * Math.exp(-dsq / (2 * tsig * tsig));
  }

  return { baseZ, canopyZ };
}

// ==========================================
// MAIN GENERATOR
// ==========================================

/** Detect region by coordinate proximity (±0.15°) */
function detectRegion(lng: number, lat: number): 'sydney' | 'blue-mountains' | 'daintree' | 'brisbane' | 'custom' {
  if (Math.abs(lng - 151.2093) < 0.15 && Math.abs(lat - -33.8688) < 0.15) return 'sydney';
  if (Math.abs(lng - 150.3119) < 0.15 && Math.abs(lat - -33.7129) < 0.15) return 'blue-mountains';
  if (Math.abs(lng - 145.4185) < 0.15 && Math.abs(lat - -16.17) < 0.15) return 'daintree';
  if (Math.abs(lng - 153.0251) < 0.15 && Math.abs(lat - -27.4698) < 0.15) return 'brisbane';
  return 'custom';
}

/** Coordinate-seeded RNG */
function createSeededRng(lng: number, lat: number) {
  const coordHash = Math.abs(Math.sin(lng) * 12345.67 + Math.cos(lat) * 98765.43);
  let offset = 0;
  return () => {
    const x = Math.sin(coordHash + offset++) * 10000;
    return x - Math.floor(x);
  };
}

export function generateProceduralTerrain(
  centerLng: number,
  centerLat: number,
  gridSize: number = 40
): RawTerrainData {
  const n = gridSize;
  const numPoints = n * n;

  const flatX = new Float32Array(numPoints);
  const flatY = new Float32Array(numPoints);
  const flatDem = new Float32Array(numPoints);
  const flatChm = new Float32Array(numPoints);
  const res = 10 / n;

  const region = detectRegion(centerLng, centerLat);
  const rand = region === 'custom' ? createSeededRng(centerLng, centerLat) : null;

  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      const idx = r * n + c;
      const x = (c - n / 2) * res;
      const y = (r - n / 2) * res;
      flatX[idx] = x;
      flatY[idx] = y;

      let result: { baseZ: number; canopyZ: number };

      switch (region) {
        case 'sydney':
          result = generateSydneyCBD(x, y);
          break;
        case 'blue-mountains':
          result = generateBlueMountains(x, y);
          break;
        case 'daintree':
          result = generateDaintree(x, y);
          break;
        case 'brisbane':
          result = generateBrisbaneHills(x, y);
          break;
        default:
          result = generateCustomCrop(x, y, rand!);
          break;
      }

      flatDem[idx] = result.baseZ;
      flatChm[idx] = result.canopyZ >= 1 ? result.canopyZ : 0;
    }
  }

  return {
    gridSize: n,
    flatX,
    flatY,
    flatDem,
    flatChm,
    resolution: res,
  };
}

/** Compute bbox area in km² from [minLng, minLat, maxLng, maxLat] */
export function computeBboxArea(bbox: [number, number, number, number]): number {
  const [minLng, minLat, maxLng, maxLat] = bbox;
  const R = 6371; // Earth radius in km
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(maxLat - minLat);
  const dLng = toRad(maxLng - minLng);

  // North-south distance (latitude arc)
  const latDist = R * dLat;

  // East-west distance at midpoint latitude
  const midLat = (minLat + maxLat) / 2;
  const lngDist = R * Math.cos(toRad(midLat)) * dLng;

  return Math.abs(latDist * lngDist);
}

/** Convert RawTerrainData to PointCloudData format for the renderer */
export function terrainToPointCloud(raw: RawTerrainData): {
  positions: Float32Array;
  intensities: Float32Array;
  classifications: Uint8Array;
} {
  const n = raw.gridSize;
  const count = n * n;
  const positions = new Float32Array(count * 3);
  const intensities = new Float32Array(count);
  const classifications = new Uint8Array(count);

  for (let i = 0; i < count; i++) {
    positions[i * 3] = raw.flatX[i];
    positions[i * 3 + 1] = raw.flatY[i];
    positions[i * 3 + 2] = raw.flatDem[i];

    // Synthetic intensity based on slope variation
    intensities[i] = Math.min(255, Math.max(0, (raw.flatDem[i] + 2) * 50));

    // Classification: 2 = ground, 3 = low vegetation, 4 = medium vegetation, 5 = high vegetation/building
    const chm = raw.flatChm[i];
    if (chm > 8) {
      classifications[i] = 5; // high vegetation/building
    } else if (chm > 3) {
      classifications[i] = 4; // medium vegetation
    } else if (chm > 0.5) {
      classifications[i] = 3; // low vegetation
    } else {
      classifications[i] = 2; // ground
    }
  }

  return { positions, intensities, classifications };
}
